import { query, orderBy, where, QueryConstraint } from 'firebase/firestore';
import { useFirestoreQueryData } from '@react-query-firebase/firestore';
import { useState, useEffect } from 'react';
import {
  DR_Consumer_Private,
  DR_Consumer_Public,
  getPersonasFromToken,
  isAdmin,
} from '@rabbit/data/portal';
import {
  DTConsumer_Public,
  FBD_HoldingProxy,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  PrincipalsFieldName,
} from '@rabbit/data/types';
import { t } from 'i18next';
import { CORPORATE, CUSTOMER } from '@rabbit/sage/utils/consts';

export interface ConsumerPrivateWithNonSRVHoldingsCount
  extends DTConsumer_Public {
  nonSRVHoldingsCount: number;
}

/** For use in Sage. Fetches a list of all Olive users (Identities with a Consumer Persona) and provides functions for filtering the list.
 * NOTE: Firestore does not support full text search, so filtering is the way to go for now in order to provide the right 'feel' on the FE.
 * If full text search is required, we should also integrate this collection with Algolia.
 */
export function useGetConsumerList(
  accessToken: string | null = null,
  consumerType: string = '',
  corporateAccount: boolean = false,
  installerPersona: string | null = null
) {
  // State to store consumers from holdingproxy
  const [holdingProxyConsumers, setHoldingProxyConsumers] = useState<any[]>([]);

  // If installerPersona is not null, fetch consumers from holdingproxy
  useEffect(() => {
    const fetchConsumersFromHoldingProxy = async () => {
      if (installerPersona) {
        try {
          // Get the active tenant link from localStorage
          const activeTenantLink = localStorage.getItem('activeTenant');
          if (activeTenantLink) {
            // Execute the query with proper constraints
            const holdingsQuery = FBD_HoldingProxy.query()
              .where(
                'mfr',
                '==',
                PersonaTypeSingleLetter.Manufacturer +
                  PersonaIdTypeSplitter +
                  activeTenantLink
              )
              .where('installer', '==', installerPersona);

            const holdings = await holdingsQuery.getContents();

            // Extract unique consumers from holdings
            const uniqueConsumers = new Map();
            holdings.forEach((holding) => {
              if (
                holding.consumer &&
                holding.consumer_email &&
                holding.consumer_name
              ) {
                uniqueConsumers.set(holding.consumer, {
                  docid: holding.consumer,
                  email: holding.consumer_email,
                  fullname: holding.consumer_name,
                  // Include any other relevant fields
                });
              }
            });

            setHoldingProxyConsumers(Array.from(uniqueConsumers.values()));
          }
        } catch (error) {
          console.error('Error fetching consumers from holdingproxy:', error);
          setHoldingProxyConsumers([]);
        }
      }
    };

    fetchConsumersFromHoldingProxy();
  }, [installerPersona]);

  // If installerPersona is not null, we'll use holdingProxyConsumers
  // Otherwise, we'll query the DR_Consumer_Private collection
  let consumerList: any;

  if (installerPersona) {
    // Use the holdingProxyConsumers as the consumerList
    consumerList = {
      data: holdingProxyConsumers,
      isLoading: false,
      error: null,
    };
  } else {
    // Original query logic for DR_Consumer_Private
    const queryConstraints = [orderBy('tupdate')] as QueryConstraint[];

    const keys = isAdmin(accessToken)
      ? t(PrincipalsFieldName).split(',')
      : getPersonasFromToken(accessToken);
    if (!keys.length) {
      console.error('No personas found in token!');
      keys.push('invalid');
    }

    if (corporateAccount) {
      if (consumerType) {
        queryConstraints.push(where('consumer_type', '==', consumerType));
      } else {
        queryConstraints.push(where('consumer_type', 'in', ['', CUSTOMER]));
      }
    }

    queryConstraints.push(where('principals', 'array-contains-any', keys));
    const consumerQuery = query(
      DR_Consumer_Private.collection(),
      ...queryConstraints
    );

    consumerList = useFirestoreQueryData(
      ['consumerList', consumerType, installerPersona],
      consumerQuery,
      {
        subscribe: true,
      }
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                              Filter Functions                              */
  /* -------------------------------------------------------------------------- */

  function getConsumersByEmail(email: string) {
    return consumerList?.data?.filter(
      (consumer: any) =>
        consumer.email?.toLowerCase()?.includes(email.toLowerCase()) ||
        consumer.business_name?.toLowerCase()?.includes(email.toLowerCase())
    );
  }

  function getConsumersByFirstName(firstName: string) {
    return consumerList?.data?.filter((consumer: any) => {
      if (consumer.splitname?.first)
        return consumer.splitname?.first
          .toLowerCase()
          .includes(firstName.toLowerCase());
      else if (consumer.fullname) {
        return consumer.fullname
          .split(' ')[0]
          .toLowerCase()
          .includes(firstName.toLowerCase());
      }
      return false;
    });
  }

  function getConsumersByLastName(lastName: string) {
    return consumerList?.data?.filter((consumer: any) => {
      if (consumer.splitname?.last)
        return consumer.splitname?.last
          .toLowerCase()
          .includes(lastName.toLowerCase());
      else if (consumer.fullname) {
        // Split by space and combine all but the first name
        const splitName = consumer.fullname.split(' ');
        if (splitName.length > 1) {
          splitName.shift();
          return splitName
            .join(' ')
            .toLowerCase()
            .includes(lastName.toLowerCase());
        }
        return false;
      }
      return false;
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                   Paginated query (temporarily disabled)                   */
  /* -------------------------------------------------------------------------- */
  // // Create the initial query
  // const consumerQuery = query(
  //   DR_Consumer_Private.collection(),
  //   limit(pageSize),
  //   orderBy('tupdate')
  // );

  // const consumerList = useFirestoreInfiniteQuery(
  //   `consumerList-size=${pageSize}`,
  //   consumerQuery,
  //   (snapshot) => {
  //     const lastDocument = snapshot.docs[snapshot.docs.length - 1];
  //     // Get the documents starting after the last document fetched.
  //     if (lastDocument)
  //       return query(
  //         consumerQuery,
  //         orderBy('tupdate'),
  //         startAfter(lastDocument)
  //       );
  //   }
  // );

  /* -------------------------------------------------------------------------- */
  /*                              Vendable Search                                */
  /* -------------------------------------------------------------------------- */

  // This is a placeholder function that would need to be implemented properly
  // Ideally, this would query a vendable collection or use an API
  function searchVendables(searchTerm: string) {
    // For now, we'll just return an empty array
    // In a real implementation, this would search vendables by title, brand, mpn, etc.
    return [];
  }

  /* -------------------------------------------------------------------------- */
  /*                              Holdings Search                               */
  /* -------------------------------------------------------------------------- */

  // Function to search holdings by various criteria
  async function searchHoldings(searchTerm: string, premiumTenant: string) {
    if (!searchTerm || searchTerm.length < 2) return [];

    try {
      // If installerPersona is not null, we already have the holdings data
      // We can filter it based on the search term
      if (installerPersona) {
        // Get the active tenant link from localStorage
        const activeTenantLink = localStorage.getItem('activeTenant');
        if (activeTenantLink) {
          // Execute the query with proper constraints
          const holdingsQuery = FBD_HoldingProxy.query()
            .where(
              'mfr',
              '==',
              PersonaTypeSingleLetter.Manufacturer +
                PersonaIdTypeSplitter +
                premiumTenant
            )
            .where('installer', '==', installerPersona);

          const holdings = await holdingsQuery.getContents();

          // Filter the results based on the search term
          return holdings.filter((holding) => {
            const searchTermLower = searchTerm.toLowerCase();

            // Search by consumer name
            if (
              holding.consumer_name &&
              holding.consumer_name.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }

            // Search by consumer email
            if (
              holding.consumer_email &&
              holding.consumer_email.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }

            // Search by title
            if (
              holding.title &&
              holding.title.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }

            // Search by serial number
            if (
              holding.serial &&
              holding.serial.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }

            // Search by registration number
            if (
              holding.registration_number &&
              holding.registration_number
                .toLowerCase()
                .includes(searchTermLower)
            ) {
              return true;
            }

            // Search by brand
            if (
              holding.brand &&
              holding.brand.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }

            // Search by mpn (Manufacturer Part Number)
            if (
              holding.mpn &&
              holding.mpn.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }

            return false;
          });
        }
      } else {
        // If installerPersona is null, use the original query logic
        const holdingsQuery = FBD_HoldingProxy.query().where(
          'mfr',
          '==',
          PersonaTypeSingleLetter.Manufacturer +
            PersonaIdTypeSplitter +
            premiumTenant
        );

        const holdings = await holdingsQuery.getContents();

        // Filter the results based on the search term
        return holdings.filter((holding) => {
          const searchTermLower = searchTerm.toLowerCase();

          // Search by title
          if (
            holding.title &&
            holding.title.toLowerCase().includes(searchTermLower)
          ) {
            return true;
          }

          // Search by serial number
          if (
            holding.serial &&
            holding.serial.toLowerCase().includes(searchTermLower)
          ) {
            return true;
          }

          // Search by registration number
          if (
            holding.registration_number &&
            holding.registration_number.toLowerCase().includes(searchTermLower)
          ) {
            return true;
          }

          // Search by brand
          if (
            holding.brand &&
            holding.brand.toLowerCase().includes(searchTermLower)
          ) {
            return true;
          }

          // Search by mpn (Manufacturer Part Number)
          if (
            holding.mpn &&
            holding.mpn.toLowerCase().includes(searchTermLower)
          ) {
            return true;
          }

          return false;
        });
      }

      return [];
    } catch (error) {
      console.error('Error searching holdings:', error);
      return [];
    }
  }

  return {
    consumerList,
    getConsumersByEmail,
    getConsumersByFirstName,
    getConsumersByLastName,
    searchVendables,
    searchHoldings,
    // Add a flag to indicate if we're using holdingproxy data
    isUsingHoldingProxy: !!installerPersona,
  };
}
